import { Fragment, useState, type JSX, type ReactNode } from 'react';
import { Popover } from '../popover';
import {
  DropdownCellDivider,
  DropdownCellOption,
  DropdownCellOptionType,
  DropdownCellTitle,
} from './dropdownCell';
import { useDropdownStyles } from './dropdown.styles';
import Box from '@mui/material/Box';
import { SearchBar } from '../forms/searchBar';
import { useIntl } from '@doxyme/locale';
import { type PopperPlacementType } from '@mui/base';
import { type CSSObject } from '@emotion/react';
import { type AriaProps } from '../../../types';
import { useTheme } from '../../../theme';

export type OptionValue = string;

export type Option = {
  value: OptionValue;
  label: string;
  sublabel?: string;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  type?: DropdownCellOptionType;
  options?: Option[];
  disabled?: boolean;
};

export type OptionGroup = {
  title: string;
  options: Option[];
};

export type DropdownProps = AriaProps & {
  options: Option[] | OptionGroup[];
  selected: string | string[] | null;
  type?: DropdownCellOptionType;
  isOpen: boolean;
  offset?: number;
  placement?: PopperPlacementType;
  anchorElement: Element | null;
  maxHeight?: CSSObject['maxHeight'];
  onClose?: () => void;
  onOptionClick: (value: string) => void;
  onSearch?: (value: string) => void;
};

export const isOptionGroup = (
  options: Option[] | OptionGroup[],
): options is OptionGroup[] =>
  options[0] !== undefined && 'options' in options[0];

export const Dropdown = ({
  options,
  selected,
  type = DropdownCellOptionType.Default,
  isOpen,
  offset,
  placement,
  anchorElement,
  maxHeight = '300px',
  onClose,
  onOptionClick,
  onSearch,
  ...rest
}: DropdownProps): JSX.Element => {
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState('');
  const { formatMessage } = useIntl();
  const styles = useDropdownStyles({ maxHeight });
  const canSearch = Boolean(onSearch);

  const isMultiSelect = Array.isArray(selected);

  const isSelected = (value: string): boolean =>
    isMultiSelect ? selected.includes(value) : selected === value;

  const DropdownCell = ({ option }: { option: Option }): JSX.Element => {
    if (option.type === DropdownCellOptionType.Divider) {
      return <DropdownCellDivider />;
    }

    return (
      <>
        <DropdownCellOption
          {...option}
          isSelected={isSelected(option.value)}
          onOptionClick={() => onOptionClick(option.value)}
          type={option.type ?? type}
          searchQuery={searchValue}
        />
        {Boolean(option.options) &&
          option.options?.map((childOption) => (
            <DropdownCellOption
              {...childOption}
              isSelected={isSelected(childOption.value)}
              onOptionClick={() => onOptionClick(childOption.value)}
              type={childOption.type ?? type}
              isChild={true}
            />
          ))}
      </>
    );
  };

  return (
    <Popover
      {...rest}
      isOpen={isOpen}
      onClose={onClose}
      offset={offset}
      placement={placement}
      anchorElement={anchorElement}
    >
      <Box
        role='menu'
        css={{
          ...styles.container,
          ...(!canSearch && {
            padding: theme.spacing(2, 0, 0, 0),
          }),
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'unset',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        }}
      >
        {canSearch && (
          <Box
            css={{
              ...styles.search,
              padding: theme.spacing(3),
            }}
          >
            <SearchBar
              value={searchValue}
              onChange={(value) => {
                onSearch?.(value);
                setSearchValue(value);
              }}
              placeholder={formatMessage({
                id: 'dropdown.search.placeholder',
              })}
              size='regular'
            />
          </Box>
        )}

        <Box
          css={{
            ...styles.menu,
            padding: theme.spacing(0, 0, 2, 0),
          }}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'unset',
            width: '100%',
            height: '100%',
            flexDirection: 'column',
          }}
        >
          {isOptionGroup(options)
            ? options.map((group) => (
                <Fragment key={group.title}>
                  <DropdownCellTitle title={group.title} />
                  {group.options.map((option) => (
                    <DropdownCell option={option} key={option.value} />
                  ))}
                  <DropdownCellDivider />
                </Fragment>
              ))
            : options.map((option) => (
                <DropdownCell option={option} key={option.value} />
              ))}
        </Box>
      </Box>
    </Popover>
  );
};
